import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import VoterEditDialog from "@/components/VoterEditDialog";
import { useAuth } from "@/context/AuthContext";
import { canRadarExport } from "@/lib/roles";
import { FirmNetwork } from "@/components/FirmNetwork";
import { UnreachedBridges } from "@/components/UnreachedBridges";
import { Radar as RadarIcon, Search, TrendingUp, CheckCircle2, HelpCircle, XCircle, Users2, ArrowRight, Download, FileText, UserCheck, Loader2, Network, ListChecks, UploadCloud, Phone, Share2, Route } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const ASSIGNABLE_BRANCHES = ["Aliağa", "Antalya", "Bodrum", "Fethiye", "İskenderun", "İzmir", "Karadeniz Ereğli", "Kocaeli", "Marmaris"];

const PRED_COLOR = { KESIN: "#16A34A", BELKI: "#F59E0B", GELMEYECEK: "#64748B", ULASILAMADI: "#0EA5E9", KARSI: "#DC2626", "": "#94A3B8" };

const PRED_LABEL = { KESIN: "Kesin", KARSI: "Karşı", GELMEYECEK: "Gelmeyecek", ULASILAMADI: "Ulaşılamadı", BELKI: "Belki", "": "Tahmin yok" };

const FOLLOWUP_LABEL = { arandi: "Arandı", olumlu: "Olumlu (kapandı)", olumsuz: "Olumsuz (kapandı)" };
const FOLLOWUP_STYLE = {
  arandi: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  olumlu: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  olumsuz: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
};

const GAP_STYLE = {
  conflict: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
  unreached: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  belki: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
};

const GapIcon = ({ type }) => {
  if (type === "conflict") return <XCircle className="h-3.5 w-3.5 shrink-0" />;
  if (type === "belki") return <HelpCircle className="h-3.5 w-3.5 shrink-0" />;
  return <HelpCircle className="h-3.5 w-3.5 shrink-0" />;
};

export default function Radar() {
  const { user } = useAuth();
  const isBranchManager = user?.role === "branch_manager";
  const showExports = canRadarExport(user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [crossOnly, setCrossOnly] = useState(false);
  const [committeeNo, setCommitteeNo] = useState("all");
  const [searchParams] = useSearchParams();
  const [branch, setBranch] = useState(searchParams.get("branch") || "all");
  const [committees, setCommittees] = useState([]);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [team, setTeam] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [editId, setEditId] = useState(null);
  const [view, setView] = useState("ops");
  const [matrix, setMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixFrom, setMatrixFrom] = useState("all");
  const [matrixTo, setMatrixTo] = useState("all");
  const [scanResult, setScanResult] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanMode, setScanMode] = useState("firma");
  const [scanPredFilter, setScanPredFilter] = useState("ALL");
  const [followDraft, setFollowDraft] = useState({});

  const doScan = async (fileList) => {
    const f = fileList?.[0];
    if (!f) return;
    setScanBusy(true); setScanResult(null); setScanPredFilter("ALL");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/opportunities/list-scan?mode=${scanMode}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScanResult(data);
      toast.success(`${data.matched_count} firma eşleşti (${data.total_candidates} ${scanMode === "sahip" ? "isim" : "ünvan"} tarandı)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tarama başarısız");
    } finally {
      setScanBusy(false);
    }
  };

  const scanRows = scanResult ? (scanPredFilter === "ALL" ? scanResult.matched : scanResult.matched.filter((m) => (m.prediction || "BOS") === scanPredFilter)) : [];

  const doScanExport = async (fmt) => {
    if (!scanResult) return;
    try {
      const res = await api.post(`/opportunities/list-scan/export?format=${fmt}`, { ...scanResult, matched: scanRows, matched_count: scanRows.length }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `liste_tarama.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarma başarısız");
    }
  };
  const [assignCaller, setAssignCaller] = useState({});
  const [assigning, setAssigning] = useState(null);
  const [dirUsers, setDirUsers] = useState([]);
  const [taskAssignee, setTaskAssignee] = useState({});
  const [tasking, setTasking] = useState(null);

  const createTask = async (o) => {
    const assignee_id = taskAssignee[o.key];
    if (!assignee_id) return;
    setTasking(o.key);
    try {
      const firms = o.gaps.slice(0, 3).map((f) => f.company_name).join(", ");
      await api.post("/tasks", {
        assignee_id,
        voter_id: o.gaps[0]?.voter_id || null,
        voter_name: o.name,
        note: `Radar fırsatı: +${o.potential_votes} potansiyel oy · ${o.gaps.length} eksik firma (${firms}${o.gaps.length > 3 ? "…" : ""})`,
      });
      toast.success(`${o.name} için arama görevi oluşturuldu`);
      setTaskAssignee((m) => ({ ...m, [o.key]: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Görev oluşturulamadı");
    } finally {
      setTasking(null);
    }
  };

  useEffect(() => {
    if (view !== "matrix") return;
    setMatrixLoading(true);
    const params = {};
    if (matrixFrom !== "all") params.from_committee = matrixFrom;
    if (matrixTo !== "all") params.to_committee = matrixTo;
    api.get("/opportunities/support-matrix", { params })
      .then(({ data }) => setMatrix(data))
      .catch(() => {})
      .finally(() => setMatrixLoading(false));
  }, [view, matrixFrom, matrixTo]);

  useEffect(() => {
    api.get("/committees").then(({ data }) => setCommittees(data)).catch(() => {});
    api.get("/meta").then(({ data }) => { setTeam(data.team_members || []); if (data.sectors) setSectors(data.sectors); }).catch(() => {});
    api.get("/messages/directory").then(({ data }) => {
      const seen = new Set();
      const out = [];
      const push = (u) => { if (u && !seen.has(u.id)) { seen.add(u.id); out.push(u); } };
      (data.general || []).forEach(push);
      (data.others || []).forEach(push);
      (data.committees || []).forEach((c) => (c.users || []).forEach(push));
      setDirUsers(out.filter((u) => !u.self));
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/opportunities", { params: {
        search: search || undefined,
        cross_only: crossOnly,
        committee_no: committeeNo !== "all" ? Number(committeeNo) : undefined,
        branch: branch !== "all" ? branch : undefined,
        include_closed: includeClosed,
      } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [search, crossOnly, committeeNo, branch, includeClosed]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const ops = data?.opportunities || [];

  const setFollowup = async (o, status) => {
    try {
      await api.post("/opportunities/followup", { key: o.key, name: o.name, status });
      toast.success(status ? `${o.name} · ${FOLLOWUP_LABEL[status]}` : `${o.name} takip durumu temizlendi`);
      load();
    } catch {
      toast.error("Takip durumu işlenemedi");
    }
  };

  const clearFollowup = async (o) => {
    try {
      await api.post("/opportunities/followup", { key: o.key, clear: true });
      setFollowDraft((m) => { const n = { ...m }; delete n[o.key]; return n; });
      toast.success(`${o.name} takip kaydı temizlendi`);
      load();
    } catch {
      toast.error("Temizlenemedi");
    }
  };

  const setDraft = (o, patch) => setFollowDraft((m) => ({
    ...m,
    [o.key]: { note: o.followup?.note || "", date: o.followup?.next_call_date || "", ...(m[o.key] || {}), ...patch },
  }));

  const saveFollowNote = async (o) => {
    const d = followDraft[o.key] || {};
    try {
      await api.post("/opportunities/followup", {
        key: o.key, name: o.name,
        note: d.note ?? (o.followup?.note || ""),
        next_call_date: d.date ?? (o.followup?.next_call_date || ""),
      });
      setFollowDraft((m) => { const n = { ...m }; delete n[o.key]; return n; });
      toast.success("Takip notu kaydedildi");
      load();
    } catch {
      toast.error("Not kaydedilemedi");
    }
  };

  const assignOpportunity = async (o) => {
    const caller = assignCaller[o.key];
    if (!caller) return;
    setAssigning(o.key);
    try {
      const ids = o.gaps.map((f) => f.voter_id);
      const { data } = await api.post("/voters/bulk-assign", { ids, team_assignment_2026: caller });
      toast.success(`${o.name} — ${data.updated} eksik firma ${caller} kişisine atandı, arama planına eklendi`);
      setAssignCaller((m) => ({ ...m, [o.key]: "" }));
      load();
    } catch {
      toast.error("Atama başarısız");
    } finally {
      setAssigning(null);
    }
  };

  const [exporting, setExporting] = useState(false);
  const doExport = async (fmt) => {
    setExporting(true);
    try {
      const res = await api.get("/opportunities/export", {
        params: {
          search: search || undefined,
          cross_only: crossOnly,
          committee_no: committeeNo !== "all" ? Number(committeeNo) : undefined,
          branch: branch !== "all" ? branch : undefined,
          format: fmt,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `radar_firsatlari.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    } finally {
      setExporting(false);
    }
  };

  const doMatrixExport = async (fmt) => {
    try {
      const params = { format: fmt };
      if (matrixFrom !== "all") params.from_committee = matrixFrom;
      if (matrixTo !== "all") params.to_committee = matrixTo;
      const res = await api.get("/opportunities/support-matrix/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `destek_matrisi.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="space-y-6" data-testid="radar-page">
      <div>
        <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-2">
          <RadarIcon className="h-7 w-7 text-[#0F2C59] dark:text-blue-300" /> Radar
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Aynı kişinin (yetkili veya ortak) bir firmasında <span className="font-semibold text-emerald-600">KESİN</span> oy varken, başka firmasında
          ulaşılamadı / karşı / tahmin girilmemiş olan durumları otomatik bulur. KESİN olan komite, o kişiye ulaşamayan komiteye
          destek olabilir — <span className="font-semibold">size daha çok oy kazandırır</span>.
        </p>
      </div>

      <div className="flex items-center gap-2" data-testid="radar-view-toggle">
        <Button variant={view === "ops" ? "default" : "outline"} size="sm" onClick={() => setView("ops")} className={view === "ops" ? "bg-[#0F2C59] hover:bg-[#0F2C59]/90" : ""} data-testid="radar-view-ops">
          <RadarIcon className="h-4 w-4 mr-2" />Fırsatlar
        </Button>
        <Button variant={view === "matrix" ? "default" : "outline"} size="sm" onClick={() => setView("matrix")} className={view === "matrix" ? "bg-[#0F2C59] hover:bg-[#0F2C59]/90" : ""} data-testid="radar-view-matrix">
          <Network className="h-4 w-4 mr-2" />Destek Matrisi
        </Button>
        <Button variant={view === "scan" ? "default" : "outline"} size="sm" onClick={() => setView("scan")} className={view === "scan" ? "bg-[#0F2C59] hover:bg-[#0F2C59]/90" : ""} data-testid="radar-view-scan">
          <ListChecks className="h-4 w-4 mr-2" />Liste Tarama
        </Button>
        <Button variant={view === "network" ? "default" : "outline"} size="sm" onClick={() => setView("network")} className={view === "network" ? "bg-[#0F2C59] hover:bg-[#0F2C59]/90" : ""} data-testid="radar-view-network">
          <Share2 className="h-4 w-4 mr-2" />Bağlantı Ağı
        </Button>
        <Button variant={view === "unreached" ? "default" : "outline"} size="sm" onClick={() => setView("unreached")} className={view === "unreached" ? "bg-[#0F2C59] hover:bg-[#0F2C59]/90" : ""} data-testid="radar-view-unreached">
          <Route className="h-4 w-4 mr-2" />Ulaşılamayanlar
        </Button>
        {view === "ops" && (
          <Button variant={includeClosed ? "default" : "outline"} size="sm" onClick={() => setIncludeClosed((v) => !v)} className={`ml-auto ${includeClosed ? "bg-slate-600 hover:bg-slate-600/90" : ""}`} data-testid="radar-closed-toggle">
            {includeClosed ? "Kapananları gizle" : `Kapananları göster${data?.closed_count ? ` (${data.closed_count})` : ""}`}
          </Button>
        )}
      </div>

      {view === "network" && (
        <FirmNetwork canExport={user?.role === "super_admin"} onOpenFirm={setEditId} />
      )}

      {view === "unreached" && (
        <UnreachedBridges canExport={user?.role === "super_admin"} onOpenFirm={setEditId} />
      )}


      {view === "scan" && (
        <div className="space-y-5" data-testid="radar-scan-view">
          <Card className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <p className="font-medium">Liste Tarama</p>
                <p className="text-sm text-muted-foreground">Excel (.xlsx) veya PDF liste yükleyin; {scanMode === "sahip" ? "listedeki kişi isimleri firma sahip/ortaklarıyla eşleştirilir ve eşleşen sahiplerin tüm firmaları listelenir" : "firma ünvanları sistemdeki firmalarla eşleştirilir"} ve Kesin / Belki / Gelmeyecek / Ulaşılamadı / Karşı dağılımı çıkarılır.</p>
                <div className="inline-flex mt-2 rounded-lg border border-border bg-muted p-0.5" data-testid="scan-mode">
                  {[["firma", "Firma Bazlı"], ["sahip", "Sahip Bazlı"]].map(([k, l]) => (
                    <button key={k} onClick={() => { setScanMode(k); setScanResult(null); }} data-testid={`scan-mode-${k}`}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${scanMode === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <label className="inline-flex shrink-0">
                <input type="file" accept=".xlsx,.xlsm,.pdf,.csv,.txt" className="hidden" data-testid="scan-file-input" onChange={(e) => { doScan(e.target.files); e.target.value = ""; }} />
                <span className="inline-flex items-center gap-2 rounded-md bg-[#0F2C59] text-white px-4 py-2 text-sm cursor-pointer hover:bg-[#0F2C59]/90">
                  {scanBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Dosya Seç ve Tara
                </span>
              </label>
            </div>
          </Card>

          {scanResult && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-4" data-testid="scan-stat-total"><p className="text-xs text-muted-foreground">{scanResult.mode === "sahip" ? "Taranan İsim" : "Taranan Ünvan"}</p><p className="text-2xl font-bold text-[#0F2C59] dark:text-blue-300">{scanResult.total_candidates}</p></Card>
                <Card className="p-4" data-testid="scan-stat-matched"><p className="text-xs text-muted-foreground">Eşleşen</p><p className="text-2xl font-bold text-emerald-600">{scanResult.matched_count}</p></Card>
                <Card className="p-4" data-testid="scan-stat-unmatched"><p className="text-xs text-muted-foreground">Eşleşmeyen</p><p className="text-2xl font-bold text-slate-500">{scanResult.unmatched_count}</p></Card>
                <Card className="p-4"><p className="text-xs text-muted-foreground">Kesin</p><p className="text-2xl font-bold text-emerald-600">{scanResult.predictions.KESIN}</p></Card>
              </div>

              <Card className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-sm">Tahmin Dağılımı</p>
                  {showExports && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => doScanExport("xlsx")} data-testid="scan-export-xlsx"><Download className="h-4 w-4 mr-1.5" />Excel</Button>
                    <Button variant="outline" size="sm" onClick={() => doScanExport("pdf")} data-testid="scan-export-pdf"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
                  </div>
                  )}
                </div>
                <div style={{ width: "100%", height: 240 }}>
                  <ResponsiveContainer>
                    <BarChart data={[
                      { name: "Kesin", value: scanResult.predictions.KESIN || 0, color: "#16A34A" },
                      { name: "Belki", value: scanResult.predictions.BELKI || 0, color: "#F59E0B" },
                      { name: "Gelmeyecek", value: scanResult.predictions.GELMEYECEK || 0, color: "#64748B" },
                      { name: "Ulaşılamadı", value: scanResult.predictions.ULASILAMADI || 0, color: "#0EA5E9" },
                      { name: "Karşı", value: scanResult.predictions.KARSI || 0, color: "#DC2626" },
                      { name: "İşaretsiz", value: scanResult.predictions.BOS || 0, color: "#CBD5E1" },
                    ]}>
                      <XAxis dataKey="name" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {["#16A34A", "#F59E0B", "#64748B", "#0EA5E9", "#DC2626", "#CBD5E1"].map((c, i) => <Cell key={i} fill={c} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="overflow-hidden">
                <div className="flex flex-wrap items-center gap-2 p-3 border-b border-border" data-testid="scan-pred-filters">
                  {[["ALL", "Tümü"], ["KESIN", "Kesin"], ["BELKI", "Belki"], ["GELMEYECEK", "Gelmeyecek"], ["ULASILAMADI", "Ulaşılamadı"], ["KARSI", "Karşı"], ["BOS", "İşaretsiz"]].map(([k, l]) => {
                    const n = k === "ALL" ? scanResult.matched.length : scanResult.matched.filter((m) => (m.prediction || "BOS") === k).length;
                    const active = scanPredFilter === k;
                    return (
                      <button key={k} onClick={() => setScanPredFilter(k)} data-testid={`scan-pred-${k}`}
                        className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${active ? "border-transparent text-white" : "border-border text-muted-foreground hover:text-foreground"}`}
                        style={active ? { background: k === "ALL" ? "#0F2C59" : PRED_COLOR[k === "BOS" ? "" : k] } : undefined}>
                        {l} <span className="tabular-nums opacity-80">({n})</span>
                      </button>
                    );
                  })}
                </div>
                <div className="overflow-x-auto max-h-[440px]">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>{scanResult.mode === "sahip" ? "Listedeki İsim" : "Listedeki Ünvan"}</TableHead>
                      {scanResult.mode === "sahip" && <TableHead>Sahip</TableHead>}
                      <TableHead>Sistem Ünvanı</TableHead>
                      <TableHead>Komite</TableHead><TableHead>Durum</TableHead><TableHead>2026 Tahmini</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {scanRows.map((m, i) => (
                        <TableRow key={i} data-testid={`scan-row-${i}`}>
                          <TableCell className="text-muted-foreground">{m.input_name}</TableCell>
                          {scanResult.mode === "sahip" && <TableCell>{m.owner_name}</TableCell>}
                          <TableCell className="font-medium">{m.company_name}</TableCell>
                          <TableCell>#{m.committee_no}</TableCell>
                          <TableCell>{m.status === "faal" ? "FAAL" : "ASKI"}</TableCell>
                          <TableCell><span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: `${PRED_COLOR[m.prediction || ""]}22`, color: PRED_COLOR[m.prediction || ""] }}>{PRED_LABEL[m.prediction || ""]}</span></TableCell>
                        </TableRow>
                      ))}
                      {scanRows.length === 0 && (
                        <TableRow><TableCell colSpan={scanResult.mode === "sahip" ? 6 : 5} className="text-center text-muted-foreground py-8">
                          {scanResult.matched.length === 0 ? "Eşleşen firma yok." : "Bu filtreye uyan kayıt yok."}
                        </TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </>
          )}
        </div>
      )}

      {view === "ops" && (<>
      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <Card className="p-4" data-testid="radar-stat-opps">
          <p className="text-xs text-muted-foreground">Fırsat Sayısı</p>
          <p className="text-2xl font-bold text-[#0F2C59] dark:text-blue-300">{loading ? "…" : data?.total_opportunities ?? 0}</p>
        </Card>
        <Card className="p-4" data-testid="radar-stat-votes">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Potansiyel Oy</p>
          <p className="text-2xl font-bold text-emerald-600">{loading ? "…" : data?.total_potential_votes ?? 0}</p>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Kişi ara…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="radar-search" />
        </div>
        <Button variant={crossOnly ? "default" : "outline"} size="sm" onClick={() => setCrossOnly((c) => !c)} data-testid="radar-cross-toggle"
          className={crossOnly ? "bg-[#0F2C59] hover:bg-[#0F2C59]/90" : ""}>
          Sadece komiteler arası
        </Button>
        {!isBranchManager && (
        <Select value={committeeNo} onValueChange={setCommitteeNo}>
          <SelectTrigger className="w-full sm:w-[220px]" data-testid="radar-committee-select"><SelectValue placeholder="Komite" /></SelectTrigger>
          <SelectContent className="max-h-[320px]">
            <SelectItem value="all">Tüm Komiteler</SelectItem>
            {committees.map((c) => (
              <SelectItem key={c.committee_no} value={String(c.committee_no)}>{c.committee_no}. {c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        )}
        {!isBranchManager && (
        <Select value={branch} onValueChange={setBranch}>
          <SelectTrigger className="w-full sm:w-[220px]" data-testid="radar-branch-select"><SelectValue placeholder="Şube (faydalanıcı)" /></SelectTrigger>
          <SelectContent className="max-h-[320px]">
            <SelectItem value="all">Tüm Şubeler</SelectItem>
            {ASSIGNABLE_BRANCHES.map((b) => (
              <SelectItem key={b} value={b}>{b} şubesine oy</SelectItem>
            ))}
          </SelectContent>
        </Select>
        )}
        {showExports && (
        <div className="flex items-center gap-2 sm:ml-auto">
          <Button variant="outline" size="sm" disabled={exporting || ops.length === 0} onClick={() => doExport("xlsx")} data-testid="radar-export-xlsx">
            <Download className="h-4 w-4 mr-2" />Excel
          </Button>
          <Button variant="outline" size="sm" disabled={exporting || ops.length === 0} onClick={() => doExport("pdf")} data-testid="radar-export-pdf">
            <FileText className="h-4 w-4 mr-2" />PDF
          </Button>
        </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-32 rounded-lg" />)}</div>
      ) : ops.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="radar-empty">
          Kriterlere uyan fırsat bulunamadı.
        </Card>
      ) : (
        <div className="space-y-3" data-testid="radar-list">
          {ops.map((o, idx) => (
            <Card key={o.key} className="border border-border overflow-hidden" data-testid={`radar-op-${idx}`}>
              <div className="flex flex-wrap items-center gap-2 p-4 border-b border-border bg-muted/30">
                <Users2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-semibold truncate flex-1 min-w-0" data-testid={`radar-op-name-${idx}`}>{o.name}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-600 text-white font-semibold shrink-0" title="Kapatılabilecek FAAL firma sayısı">
                  +{o.potential_votes} potansiyel oy
                </span>
                {o.cross_committee && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#0F2C59] text-white shrink-0">Komiteler arası</span>
                )}
                {o.followup?.status && (
                  <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 font-medium ${FOLLOWUP_STYLE[o.followup.status]}`} data-testid={`radar-followup-badge-${idx}`}>{FOLLOWUP_LABEL[o.followup.status]}</span>
                )}
                <span className="text-[11px] text-muted-foreground shrink-0">{o.total_firms} firma</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4 p-4">
                <div>
                  <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-2 flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" /> KESİN ({o.secured.length}) — kime sorulacak
                  </p>
                  <div className="space-y-1.5">
                    {o.secured.map((f, i) => (
                      <div key={i} className="text-sm rounded-md bg-emerald-50 dark:bg-emerald-950/40 p-2" data-testid={`radar-secured-${idx}-${i}`}>
                        <button type="button" onClick={() => setEditId(f.voter_id)} className="font-medium truncate text-left hover:underline w-full block" data-testid={`radar-secured-firm-${idx}-${i}`}>{f.company_name}</button>
                        <p className="text-[11px] text-muted-foreground">
                          Komite {f.committee_no} · {f.committee_name}{f.branch ? ` · ${f.branch}` : ""}{f.layer ? ` · ${f.layer === "şube" ? "şube kaydı" : "merkez kaydı"}` : ""}{f.responsible ? ` · Sorumlu: ${f.responsible}` : ""}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-2 flex items-center gap-1">
                    <ArrowRight className="h-4 w-4" /> Eksik / Tezat ({o.gaps.length}) — destek gerekiyor
                  </p>
                  <div className="space-y-1.5">
                    {o.gaps.map((f, i) => (
                      <div key={i} className="text-sm rounded-md bg-muted/50 p-2" data-testid={`radar-gap-${idx}-${i}`}>
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => setEditId(f.voter_id)} className="font-medium truncate flex-1 text-left hover:underline" data-testid={`radar-gap-firm-${idx}-${i}`}>{f.company_name}</button>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 inline-flex items-center gap-1 ${GAP_STYLE[f.type] || GAP_STYLE.unreached}`}>
                            <GapIcon type={f.type} />{PRED_LABEL[f.prediction] ?? f.prediction}
                          </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          Komite {f.committee_no} · {f.committee_name}{f.branch ? ` · ${f.branch}` : ""}{f.layer ? ` · ${f.layer === "şube" ? "şube kaydı" : "merkez kaydı"}` : ""}{f.responsible ? ` · Sorumlu: ${f.responsible}` : ""}
                          {f.status !== "faal" ? " · (Askıda)" : ""}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 border-t border-border bg-muted/20 flex-wrap" data-testid={`radar-assign-bar-${idx}`}>
                <UserCheck className="h-4 w-4 text-cyan-600 shrink-0" />
                <span className="text-xs text-muted-foreground">Bu kişinin {o.gaps.length} eksik firmasını sorumluya ata:</span>
                <Select value={assignCaller[o.key] || ""} onValueChange={(v) => setAssignCaller((m) => ({ ...m, [o.key]: v }))}>
                  <SelectTrigger className="h-8 w-[170px]" data-testid={`radar-assign-select-${idx}`}><SelectValue placeholder="Sorumlu seç" /></SelectTrigger>
                  <SelectContent>{team.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" className="h-8 bg-[#0F2C59] hover:bg-[#0F2C59]/90" disabled={!assignCaller[o.key] || assigning === o.key} onClick={() => assignOpportunity(o)} data-testid={`radar-assign-btn-${idx}`}>
                  {assigning === o.key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ata ve Plana Ekle"}
                </Button>
                <div className="flex items-center gap-2 flex-wrap sm:ml-auto" data-testid={`radar-task-bar-${idx}`}>
                  <Phone className="h-4 w-4 text-indigo-600 shrink-0" />
                  <span className="text-xs text-muted-foreground">Ara görevi:</span>
                  <Select value={taskAssignee[o.key] || ""} onValueChange={(v) => setTaskAssignee((m) => ({ ...m, [o.key]: v }))}>
                    <SelectTrigger className="h-8 w-[190px]" data-testid={`radar-task-select-${idx}`}><SelectValue placeholder="Kullanıcı seç" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {dirUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role_label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" className="h-8 border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300" disabled={!taskAssignee[o.key] || tasking === o.key} onClick={() => createTask(o)} data-testid={`radar-task-btn-${idx}`}>
                    {tasking === o.key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Görev Ata"}
                  </Button>
                </div>
              </div>
              <div className="flex flex-col gap-2 px-3 pb-3" data-testid={`radar-followup-bar-${idx}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">Takip:</span>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setFollowup(o, "arandi")} data-testid={`radar-fu-called-${idx}`}>Arandı</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300" onClick={() => setFollowup(o, "olumlu")} data-testid={`radar-fu-pos-${idx}`}>Olumlu · Kapat</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300" onClick={() => setFollowup(o, "olumsuz")} data-testid={`radar-fu-neg-${idx}`}>Olumsuz · Kapat</Button>
                  {(o.followup?.status || o.followup?.note || o.followup?.next_call_date) && <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => clearFollowup(o)} data-testid={`radar-fu-clear-${idx}`}>Temizle</Button>}
                  {o.followup?.updated_by && <span className="text-[11px] text-muted-foreground ml-auto">Son: {o.followup.updated_by}</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    value={followDraft[o.key]?.note ?? (o.followup?.note || "")}
                    onChange={(e) => setDraft(o, { note: e.target.value })}
                    placeholder="Arama sonucu / takip notu"
                    className="h-7 text-xs flex-1 min-w-[160px]"
                    data-testid={`radar-fu-note-${idx}`}
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Sonraki arama</span>
                    <Input
                      type="date"
                      value={followDraft[o.key]?.date ?? (o.followup?.next_call_date || "")}
                      onChange={(e) => setDraft(o, { date: e.target.value })}
                      className="h-7 text-xs w-[150px]"
                      data-testid={`radar-fu-date-${idx}`}
                    />
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => saveFollowNote(o)} data-testid={`radar-fu-save-${idx}`}>Kaydet</Button>
                </div>
                {(o.followup?.note || o.followup?.next_call_date) && (
                  <div className="flex items-center gap-2 flex-wrap text-[11px]">
                    {o.followup?.next_call_date && <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300 font-medium" data-testid={`radar-fu-nextdate-${idx}`}>Sonraki arama: {o.followup.next_call_date}</span>}
                    {o.followup?.note && <span className="text-muted-foreground italic truncate max-w-full" title={o.followup.note}>“{o.followup.note}”</span>}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
      </>)}

      {view === "matrix" && (
        matrixLoading ? (
          <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}</div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground max-w-3xl">
              Bir komitede <span className="font-semibold text-emerald-600">KESİN</span> oyu olan kişilerin, başka komitelerdeki FAAL eksik/tezat firmalarında getirebileceği oy desteği. Hangi komite hangi komiteye kaç oy kazandırabilir?
            </p>
            <div className="grid grid-cols-2 gap-3 sm:max-w-md">
              <Card className="p-4" data-testid="matrix-stat-pairs"><p className="text-xs text-muted-foreground">Komite Eşleşmesi</p><p className="text-2xl font-bold text-[#0F2C59] dark:text-blue-300">{matrix?.total_pairs ?? 0}</p></Card>
              <Card className="p-4" data-testid="matrix-stat-votes"><p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Toplam Destek Oyu</p><p className="text-2xl font-bold text-emerald-600">{matrix?.total_votes ?? 0}</p></Card>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex flex-col gap-1 flex-1 sm:max-w-[280px]">
                <span className="text-xs text-muted-foreground">Destek Veren Komite</span>
                <Select value={matrixFrom} onValueChange={setMatrixFrom}>
                  <SelectTrigger data-testid="matrix-from-select"><SelectValue placeholder="Komite" /></SelectTrigger>
                  <SelectContent className="max-h-[320px]">
                    <SelectItem value="all">Tüm Komiteler</SelectItem>
                    {committees.map((c) => (
                      <SelectItem key={c.committee_no} value={String(c.committee_no)}>{c.committee_no}. {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1 flex-1 sm:max-w-[280px]">
                <span className="text-xs text-muted-foreground">Destek Alan Komite</span>
                <Select value={matrixTo} onValueChange={setMatrixTo}>
                  <SelectTrigger data-testid="matrix-to-select"><SelectValue placeholder="Komite" /></SelectTrigger>
                  <SelectContent className="max-h-[320px]">
                    <SelectItem value="all">Tüm Komiteler</SelectItem>
                    {committees.map((c) => (
                      <SelectItem key={c.committee_no} value={String(c.committee_no)}>{c.committee_no}. {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(matrixFrom !== "all" || matrixTo !== "all") && (
                <Button variant="ghost" size="sm" className="sm:self-end" onClick={() => { setMatrixFrom("all"); setMatrixTo("all"); }} data-testid="matrix-clear-filters">
                  Filtreyi temizle
                </Button>
              )}
            </div>
            {showExports && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={!(matrix?.rows || []).length} onClick={() => doMatrixExport("xlsx")} data-testid="matrix-export-xlsx"><Download className="h-4 w-4 mr-2" />Excel</Button>
              <Button variant="outline" size="sm" disabled={!(matrix?.rows || []).length} onClick={() => doMatrixExport("pdf")} data-testid="matrix-export-pdf"><FileText className="h-4 w-4 mr-2" />PDF</Button>
            </div>
            )}
            {(matrix?.rows || []).length === 0 ? (
              <Card className="p-10 text-center text-muted-foreground" data-testid="matrix-empty">Komiteler arası destek fırsatı bulunamadı.</Card>
            ) : (
              <Card className="border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Destek Veren Komite (KESİN)</TableHead>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Destek Alan Komite (Eksik)</TableHead>
                        <TableHead className="text-right">Potansiyel Oy</TableHead>
                        <TableHead className="text-right">Ortak Kişi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="matrix-table-body">
                      {matrix.rows.map((r, i) => (
                        <TableRow key={i} data-testid={`matrix-row-${i}`}>
                          <TableCell className="font-medium">#{r.from_committee} {r.from_name}</TableCell>
                          <TableCell className="text-emerald-600"><ArrowRight className="h-4 w-4" /></TableCell>
                          <TableCell className="font-medium">#{r.to_committee} {r.to_name}</TableCell>
                          <TableCell className="text-right"><span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-600 text-white font-semibold">+{r.votes}</span></TableCell>
                          <TableCell className="text-right text-muted-foreground">{r.people}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            )}
          </>
        )
      )}
      <VoterEditDialog voterId={editId} open={!!editId} onClose={() => setEditId(null)} onSaved={() => load()} team={team} sectors={sectors} />
    </div>
  );
}
